"use client";

import { useRef } from "react";
import { useModalOverlay, usePreventScroll } from "@react-aria/overlays";
import { useButton } from "@react-aria/button";
import { useDialog } from "@react-aria/dialog";
import { FocusScope } from "@react-aria/focus";
import { mergeProps } from "@react-aria/utils";
import { useOverlayTriggerState } from "react-stately";
import Button, { ButtonProps } from "./Button";
import "./Modal.css";
import ScreenOverlay from "./ScreenOverlay";
import { csx } from "../utils/utils";
import { useIsClosing } from "../hooks/useIsClosing";
import { useBreakpoint } from "../hooks";
import { BREAKPOINTS } from "../constants";

const ModalTitle = ({ children }: { children: React.ReactNode }) => {
  return <h2 className="proton-Modal__title">{children}</h2>;
};

const ModalSubtitle = ({ children }: { children: React.ReactNode }) => {
  return <h3 className="proton-Modal__subtitle">{children}</h3>;
};

const ModalBody = ({ children }: { children: React.ReactNode }) => {
  return <div className="proton-Modal__body">{children}</div>;
};

const ModalAction = (props: ButtonProps) => {
  return (
    <div className="proton-Modal__action">
      <Button {...props} />
    </div>
  );
};

const ModalActions = ({ children }: { children: React.ReactNode }) => {
  return (
    <FocusScope autoFocus>
      <div className="proton-Modal__actions">{children}</div>
    </FocusScope>
  );
};

const ModalContent = ({
  title,
  subtitle,
  body,
  onClose,
  children,
  isClosing,
  "data-testid": dataTestId,
}: ModalProps & {
  isClosing: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const state = useOverlayTriggerState({ isOpen: true, onOpenChange: onClose });
  const { modalProps } = useModalOverlay(
    {
      isDismissable: false, //handle dismiss behavior in useIsClosing
    },
    state,
    ref
  );

  const { dialogProps } = useDialog({ role: "dialog" }, ref);
  usePreventScroll();

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { buttonProps: closeButtonProps } = useButton(
    {
      onPress: onClose,
      "aria-label": "Close",
    },
    closeButtonRef
  );

  return (
    <FocusScope contain restoreFocus autoFocus>
      <div
        data-testid={dataTestId}
        className={csx("proton-Modal", isClosing && "proton-Modal--closing")}
        {...mergeProps(modalProps, dialogProps)}
        ref={ref}
      >
        <button
          name="Close"
          className="proton-Modal__close-button"
          {...closeButtonProps}
          ref={closeButtonRef}
        >
          ×
        </button>
        {title && <ModalTitle>{title}</ModalTitle>}
        {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
        {body && <ModalBody>{body}</ModalBody>}
        <div className="proton-Modal__content">{children}</div>
      </div>
    </FocusScope>
  );
};

export interface ModalProps {
  /**
   * Controls the visibility of the modal
   * */
  isOpen: boolean;
  /**
   * Callback function when modal is closed
   * */
  onClose?: () => void;
  /**
   * Whether to show the modal with an overlay background.
   * @default true
   * */
  isOverlay?: boolean;
  /**
   * Optional title text for the modal header
   * */
  title?: string;
  /**
   * Optional subtitle text below the modal title
   * */
  subtitle?: string;
  /**
   * Optional body text content for the modal
   * */
  body?: string;
  /**
   * Content to render inside the modal
   * */
  children: React.ReactNode;
  /**
   * data-testid for the modal
   * */
  "data-testid"?: string;
}

/**
 * Modal component
 * @interface ModalProps
 */
const ModalWrapper = ({
  isOpen,
  onClose,
  title,
  subtitle,
  body,
  children,
  isOverlay = true,
  "data-testid": dataTestId,
}: ModalProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const { isClosing, handleClose } = useIsClosing({
    onClose,
    overlayRef,
  });
  const isMobile = useBreakpoint(BREAKPOINTS.MEDIUM);

  if (!isOpen) return null;

  const modalContent = (
    <ModalContent
      isOpen={isOpen}
      title={title}
      subtitle={subtitle}
      body={body}
      onClose={handleClose}
      isClosing={isClosing}
      data-testid={dataTestId}
    >
      {children}
    </ModalContent>
  );

  return isOverlay || isMobile ? (
    <ScreenOverlay fadeIn ref={overlayRef} onClose={handleClose}>
      {modalContent}
    </ScreenOverlay>
  ) : (
    modalContent
  );
};

export const Modal = Object.assign(ModalWrapper, {
  Title: ModalTitle,
  Subtitle: ModalSubtitle,
  Body: ModalBody,
  Action: ModalAction,
  Actions: ModalActions,
});
